import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, Pencil, RotateCcw, Save, Search } from 'lucide-react';
import { produceTrackerAPI, productsAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import FilterSelect from '../../components/common/FilterSelect';
import Modal from '../../components/common/Modal';
import { isAllStoresSelection } from '../BI/useBiVendorId';
import {
  buildProduceRowsForDate,
  calculateProduceSummary,
  clearProduceField,
  findProduceRecord,
  formatDateKey,
  updateProduceRowField,
} from '../../utils/produceInventoryTracker';

const normalizeText = (value) => String(value ?? '').trim();
const displayCurrency = (value) => `Nrs ${Number(value || 0).toLocaleString('en-NP', { maximumFractionDigits: 2 })}`;
const todayKey = formatDateKey();
const formatDateLabel = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const emptyInvoiceForm = () => ({
  invoiceNumber: '',
  totalAmount: '',
  skuTotalMatches: null,
  skuTotalMismatchReason: '',
  sellerSystemMatches: null,
  sellerSystemMismatchReason: '',
  sellerSystemAmount: '',
});

const YesNoToggle = ({ value, onChange, name }) => (
  <div className="inline-flex overflow-hidden rounded-lg border border-slate-200">
    {[
      { key: true, label: 'Yes' },
      { key: false, label: 'No' },
    ].map((option) => {
      const active = value === option.key;
      return (
        <button
          key={`${name}-${option.label}`}
          type="button"
          onClick={() => onChange(option.key)}
          className={`px-3 py-1.5 text-xs font-semibold transition ${
            active
              ? option.key
                ? 'bg-emerald-600 text-white'
                : 'bg-rose-600 text-white'
              : 'bg-white text-slate-600 hover:bg-slate-50'
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);
const ENTRY_SECTIONS = {
  opening: {
    label: 'Opening Inventory',
    quantityField: 'openingQuantity',
    rateField: 'openingRate',
    totalField: 'openingTotal',
    titleTone: 'text-slate-900',
    helperText: 'Opening inventory pulls forward from the previous day when available, and you can adjust it if needed.',
  },
  purchase: {
    label: 'Purchase',
    quantityField: 'purchaseQuantity',
    rateField: 'purchaseRate',
    totalField: 'purchaseTotal',
    titleTone: 'text-sky-900',
    helperText: 'Enter purchased quantity and rate. Saved rates remain available when you reopen the day later.',
  },
  closing: {
    label: 'Closing Inventory',
    quantityField: 'closingQuantity',
    rateField: 'closingRate',
    totalField: 'closingTotal',
    titleTone: 'text-emerald-900',
    helperText: 'Capture the final stock count for the day and review expected closing versus actual variance.',
  },
  wastage: {
    label: 'Wastage',
    quantityField: 'wastageQuantity',
    rateField: 'wastageRate',
    totalField: 'wastageTotal',
    titleTone: 'text-amber-900',
    helperText: 'Record damaged or expired produce here. Wastage automatically affects expected closing.',
  },
};
const ProduceInventoryTracker = () => {
  const { user, hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const selectedStoreContext = useMemo(() => ({
    storeInfoId: selectedStore?.storeInfoId || selectedStore?.store_info_id || null,
    vendorId: selectedStore?.vendorId || selectedStore?.vendor_id || null,
    legacyStoreId: selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || null,
    storeName: selectedStore?.name || selectedStore?.storeName || selectedStore?.storeCode || '',
  }), [selectedStore]);
  const storeId = selectedStoreContext.storeInfoId || selectedStoreContext.legacyStoreId || '';
  const storeName = selectedStoreContext.storeName;
  const hasStoreContext = Boolean(
    !isAllStoresSelection(selectedStore)
    && selectedStoreContext.storeInfoId
    && selectedStoreContext.vendorId
  );
  const canViewPage = hasFeatureAccess('produce_tracker.task');
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [date, setDate] = useState(todayKey);
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('draft');
  const [activeSection, setActiveSection] = useState('opening');
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [savedFromDate, setSavedFromDate] = useState(formatDateKey(new Date(Date.now() - 6 * 24 * 60 * 60 * 1000)));
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(emptyInvoiceForm);
  const [invoiceErrors, setInvoiceErrors] = useState({});
  const [savingProgress, setSavingProgress] = useState(false);
  const [purchaseInvoice, setPurchaseInvoice] = useState(null);
  const [savedToDate, setSavedToDate] = useState(todayKey);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) {
      setRecords([]);
      return;
    }

    const loadRecords = async () => {
      try {
        const response = await produceTrackerAPI.getAll({ limit: 500, storeId });
        setRecords(response.data?.data || []);
      } catch (error) {
        setRecords([]);
        toast.error(error?.response?.data?.message || 'Failed to load produce tracker records');
      }
    };

    loadRecords();
  }, [canViewPage, hasStoreContext, storeId]);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) {
      setProducts([]);
      return;
    }

    const loadProducts = async () => {
      setLoadingProducts(true);
      try {
        const response = await productsAPI.getClassified({
          classificationCode: 'produce',
          storeInfoId: selectedStoreContext.storeInfoId,
          vendorId: selectedStoreContext.vendorId,
          limit: 5000,
        });

        const nextProducts = (response.data?.data || [])
          .map((product) => ({
            id: product.id,
            name: normalizeText(product.name || product.product),
            sku: normalizeText(product.sku),
          }))
          .filter((product) => product.name)
          .sort((left, right) => left.name.localeCompare(right.name));

        if (nextProducts.length) {
          setProducts(nextProducts);
        } else {
          setProducts([]);
          toast('No produce products are classified for the selected store yet.');
        }
      } catch (error) {
        setProducts([]);
        toast.error(error?.response?.data?.message || 'Failed to load produce products');
      } finally {
        setLoadingProducts(false);
      }
    };

    loadProducts();
  }, [canViewPage, hasStoreContext, selectedStoreContext.storeInfoId, selectedStoreContext.vendorId]);

  useEffect(() => {
    if (!canViewPage || !hasStoreContext) {
      setRows([]);
      setStatus('draft');
      setPurchaseInvoice(null);
      return;
    }

    const existingRecord = findProduceRecord(records, { storeId, date });
    const nextRows = buildProduceRowsForDate({
      products,
      records,
      storeId,
      date,
      existingRecord,
    });
    setRows(nextRows);
    setStatus(existingRecord?.status || 'draft');
    setPurchaseInvoice(existingRecord?.purchaseInvoice || null);
  }, [canViewPage, date, hasStoreContext, products, records, storeId]);

  const filteredRows = useMemo(() => {
    const query = normalizeText(deferredSearch).toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => [row.productName, row.sku].join(' ').toLowerCase().includes(query));
  }, [deferredSearch, rows]);
  const entrySectionOptions = useMemo(
    () => Object.entries(ENTRY_SECTIONS).map(([value, config]) => ({ value, label: config.label })),
    []
  );

  const summary = useMemo(() => calculateProduceSummary(rows), [rows]);
  const currentRecord = useMemo(() => findProduceRecord(records, { storeId, date }), [date, records, storeId]);
  const sectionConfig = ENTRY_SECTIONS[activeSection];

  const savedRecords = useMemo(() => {
    const from = savedFromDate ? new Date(`${savedFromDate}T00:00:00`).getTime() : null;
    const to = savedToDate ? new Date(`${savedToDate}T23:59:59`).getTime() : null;

    return records
      .filter((record) => String(record.storeId || '') === String(storeId || ''))
      .filter((record) => {
        const time = new Date(`${record.date}T00:00:00`).getTime();
        if (Number.isFinite(from) && time < from) return false;
        if (Number.isFinite(to) && time > to) return false;
        return true;
      })
      .sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime())
      .slice(0, 12);
  }, [records, savedFromDate, savedToDate, storeId]);

  const updateCell = (rowId, field, value) => {
    const numericValue = value === '' ? 0 : Number(value);
    setRows((current) => current.map((row) => (
      row.id === rowId ? updateProduceRowField(row, field, numericValue) : row
    )));
  };

  const openSaveProgressModal = () => {
    if (!hasStoreContext) {
      toast.error('Select a specific store from the header before saving produce inventory.');
      return;
    }
    setInvoiceForm({
      ...emptyInvoiceForm(),
      invoiceNumber: purchaseInvoice?.invoiceNumber || '',
      totalAmount: purchaseInvoice?.totalAmount != null ? String(purchaseInvoice.totalAmount) : '',
      skuTotalMatches: typeof purchaseInvoice?.skuTotalMatches === 'boolean' ? purchaseInvoice.skuTotalMatches : null,
      skuTotalMismatchReason: purchaseInvoice?.skuTotalMismatchReason || '',
      sellerSystemMatches: typeof purchaseInvoice?.sellerSystemMatches === 'boolean' ? purchaseInvoice.sellerSystemMatches : null,
      sellerSystemMismatchReason: purchaseInvoice?.sellerSystemMismatchReason || '',
      sellerSystemAmount: purchaseInvoice?.sellerSystemAmount != null ? String(purchaseInvoice.sellerSystemAmount) : '',
    });
    setInvoiceErrors({});
    setInvoiceModalOpen(true);
  };

  const validateInvoiceForm = () => {
    const nextErrors = {};
    const invoiceNumber = normalizeText(invoiceForm.invoiceNumber);
    const totalAmount = Number(invoiceForm.totalAmount);
    if (!invoiceNumber) nextErrors.invoiceNumber = 'Invoice number is required.';
    if (!Number.isFinite(totalAmount) || totalAmount < 0) nextErrors.totalAmount = 'Total amount is required.';
    if (invoiceForm.skuTotalMatches == null) nextErrors.skuTotalMatches = 'Select Yes or No.';
    if (invoiceForm.skuTotalMatches === false && !normalizeText(invoiceForm.skuTotalMismatchReason)) {
      nextErrors.skuTotalMismatchReason = 'Reason is required when amounts do not match.';
    }
    if (invoiceForm.sellerSystemMatches == null) nextErrors.sellerSystemMatches = 'Select Yes or No.';
    if (invoiceForm.sellerSystemMatches === false) {
      if (!normalizeText(invoiceForm.sellerSystemMismatchReason)) {
        nextErrors.sellerSystemMismatchReason = 'Reason is required when seller system amount does not match.';
      }
      const sellerAmount = Number(invoiceForm.sellerSystemAmount);
      if (!Number.isFinite(sellerAmount) || sellerAmount < 0) {
        nextErrors.sellerSystemAmount = 'Seller system amount is required.';
      }
    }
    setInvoiceErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSave = async (nextStatus = 'draft', invoiceOverride = null) => {
    if (!hasStoreContext) {
      toast.error('Select a specific store from the header before saving produce inventory.');
      return;
    }

    const nextInvoice = invoiceOverride || (nextStatus === 'draft' ? purchaseInvoice : purchaseInvoice);

    const payload = {
      id: currentRecord?.id || `produce-${storeId || 'store'}-${date}`,
      date,
      storeId,
      storeName,
      status: nextStatus,
      createdAt: currentRecord?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      completedAt: nextStatus === 'completed' ? new Date().toISOString() : currentRecord?.completedAt || '',
      createdBy: currentRecord?.createdBy || user?.name || 'Current User',
      updatedBy: user?.name || 'Current User',
      rows,
      purchaseInvoice: nextInvoice || undefined,
    };

    try {
      setSavingProgress(true);
      await produceTrackerAPI.save(payload);
      const response = await produceTrackerAPI.getAll({ limit: 500, storeId });
      setRecords(response.data?.data || []);
      setStatus(nextStatus);
      if (nextInvoice) setPurchaseInvoice(nextInvoice);
      setInvoiceModalOpen(false);
      toast.success(nextStatus === 'completed' ? 'Produce inventory marked complete.' : 'Produce inventory progress saved.');
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save produce inventory');
    } finally {
      setSavingProgress(false);
    }
  };

  const confirmSaveProgress = async () => {
    if (!validateInvoiceForm()) {
      toast.error('Please complete the invoice confirmation fields.');
      return;
    }

    const nextInvoice = {
      invoiceNumber: normalizeText(invoiceForm.invoiceNumber),
      totalAmount: Number(invoiceForm.totalAmount),
      skuTotalMatches: invoiceForm.skuTotalMatches === true,
      skuTotalMismatchReason: invoiceForm.skuTotalMatches === false
        ? normalizeText(invoiceForm.skuTotalMismatchReason)
        : '',
      sellerSystemMatches: invoiceForm.sellerSystemMatches === true,
      sellerSystemMismatchReason: invoiceForm.sellerSystemMatches === false
        ? normalizeText(invoiceForm.sellerSystemMismatchReason)
        : '',
      sellerSystemAmount: invoiceForm.sellerSystemMatches === false
        ? Number(invoiceForm.sellerSystemAmount)
        : 0,
      recordedAt: new Date().toISOString(),
    };

    await handleSave('draft', nextInvoice);
  };

  const openRecordForEdit = (record) => {
    setDate(record.date);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Produce Inventory Tracker</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1640px] space-y-5 px-3">
      <div className="border-b border-slate-200 pb-3">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h1 className="text-[24px] font-bold tracking-tight text-slate-900">Produce Inventory Tracker</h1>
            <p className="mt-1 text-sm text-slate-600">
              Enter opening, purchase, sales placeholder, closing, and wastage directly in one daily produce sheet.
            </p>
          </div>
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
            status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
          }`}>
            {status === 'completed' ? 'Completed' : 'In Progress'}
          </span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-6">
        <div className="rounded-[14px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(125,211,252,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#ecfeff_100%)] p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)]">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Total Products</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{summary.totalProducts}</p>
          <p className="mt-2 text-xs text-slate-500">Sales Qty placeholder: {summary.salesUnits}</p>
        </div>
        <div className="rounded-[14px] border border-slate-200 bg-slate-50 p-4 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Opening SKU</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{summary.openingSkuCount}</p>
          <p className="mt-2 text-xs text-slate-600">Qty: {summary.openingUnits}</p>
          <p className="mt-1 text-xs text-slate-600">Amount: {displayCurrency(summary.openingAmount)}</p>
        </div>
        <div className="rounded-[14px] border border-sky-200 bg-sky-50 p-4 shadow-[0_12px_30px_rgba(14,165,233,0.08)]">
          <p className="text-xs uppercase tracking-[0.18em] text-sky-600">Purchased SKU</p>
          <p className="mt-1 text-2xl font-bold text-sky-900">{summary.purchasedSkuCount}</p>
          <p className="mt-2 text-xs text-sky-700">Qty: {summary.purchasedUnits}</p>
          <p className="mt-1 text-xs text-sky-700">Amount: {displayCurrency(summary.purchasedAmount)}</p>
        </div>
        <div className="rounded-[14px] border border-emerald-200 bg-emerald-50 p-4 shadow-[0_12px_30px_rgba(16,185,129,0.08)]">
          <p className="text-xs uppercase tracking-[0.18em] text-emerald-600">Closing SKU</p>
          <p className="mt-1 text-2xl font-bold text-emerald-900">{summary.closingSkuCount}</p>
          <p className="mt-2 text-xs text-emerald-700">Qty: {summary.closingUnits}</p>
          <p className="mt-1 text-xs text-emerald-700">Amount: {displayCurrency(summary.closingAmount)}</p>
        </div>
        <div className="rounded-[14px] border border-amber-200 bg-amber-50 p-4 shadow-[0_12px_30px_rgba(245,158,11,0.08)]">
          <p className="text-xs uppercase tracking-[0.18em] text-amber-600">Wastage SKU</p>
          <p className="mt-1 text-2xl font-bold text-amber-900">{summary.wastageSkuCount}</p>
          <p className="mt-2 text-xs text-amber-700">Qty: {summary.wastageUnits}</p>
          <p className="mt-1 text-xs text-amber-700">Amount: {displayCurrency(summary.wastageAmount)}</p>
        </div>
        <div className={`rounded-[14px] border p-4 shadow-[0_12px_30px_rgba(15,23,42,0.05)] ${
          summary.varianceStatus === 'valid' ? 'border-emerald-200 bg-emerald-50' : 'border-rose-200 bg-rose-50'
        }`}>
          <p className={`text-xs uppercase tracking-[0.18em] ${summary.varianceStatus === 'valid' ? 'text-emerald-600' : 'text-rose-600'}`}>Total Variance</p>
          <div className="mt-1 flex items-center gap-2">
            {summary.varianceStatus !== 'valid' ? <AlertTriangle className="h-4 w-4 text-rose-500" /> : null}
            <p className={`text-2xl font-bold ${summary.varianceStatus === 'valid' ? 'text-emerald-900' : 'text-rose-900'}`}>{summary.varianceTotal}</p>
          </div>
          <p className={`mt-2 text-xs ${summary.varianceStatus === 'valid' ? 'text-emerald-700' : 'text-rose-700'}`}>
            {summary.varianceStatus === 'valid' ? 'No discrepancy detected' : 'Mismatch detected'}
          </p>
        </div>
      </div>

      <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <label className="block">
            <span className="label">Entry Date</span>
            <input
              className="input rounded-lg"
              type="date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              max={todayKey}
            />
          </label>

          <label className="block">
            <span className="label">Entry Section</span>
            <FilterSelect
              className="w-full"
              value={activeSection}
              options={entrySectionOptions}
              onChange={(nextValue) => setActiveSection(String(nextValue))}
            />
          </label>

          <label className="relative block">
            <span className="label">Search Product</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-slate-400" />
            <input
              className="input pl-10"
              placeholder={loadingProducts ? 'Loading produce...' : 'Search product or SKU...'}
              value={search}
              disabled={!hasStoreContext}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <label className="block">
            <span className="label">Date From</span>
            <input
              className="input rounded-lg text-sm"
              type="date"
              value={savedFromDate}
              max={savedToDate}
              onChange={(event) => setSavedFromDate(event.target.value)}
            />
          </label>

          <label className="block">
            <span className="label">Date To</span>
            <input
              className="input rounded-lg text-sm"
              type="date"
              value={savedToDate}
              min={savedFromDate}
              max={todayKey}
              onChange={(event) => setSavedToDate(event.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className={`text-lg font-semibold ${sectionConfig.titleTone}`}>{sectionConfig.label}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {sectionConfig.helperText}
            </p>
          </div>
        </div>

        <div className="mt-4 table-scroll-shell rounded-[12px] border border-slate-200">
          <table className="min-w-[1180px] divide-y divide-slate-200 text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold bg-slate-50">Product</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold bg-slate-50">SKU</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">Sales Qty</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">{sectionConfig.label} Qty</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">{sectionConfig.label} Rate</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">{sectionConfig.label} Total</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">Expected Closing</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">Current Closing</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold text-right bg-slate-50">Variance</th>
                <th className="sticky top-0 z-10 px-3 py-3 font-semibold bg-slate-50">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {filteredRows.map((row) => {
                const isVarianceValid = row.variance === 0;
                return (
                  <tr key={row.id} className={isVarianceValid ? 'hover:bg-emerald-50/40' : 'bg-rose-50/40 hover:bg-rose-50'}>
                    <td className="px-3 py-3 font-medium text-slate-900">{row.productName}</td>
                    <td className="px-3 py-3 text-slate-500">{row.sku || '-'}</td>
                    <td className="px-3 py-3 text-right text-slate-500">{row.salesQuantity}</td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input ml-auto max-w-[110px] text-right"
                        value={row[sectionConfig.quantityField]}
                        onChange={(event) => updateCell(row.id, sectionConfig.quantityField, event.target.value)}
                      />
                    </td>
                    <td className="px-3 py-3">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="input ml-auto max-w-[110px] text-right"
                        value={row[sectionConfig.rateField]}
                        onChange={(event) => updateCell(row.id, sectionConfig.rateField, event.target.value)}
                      />
                    </td>
                    <td className="px-3 py-3 text-right font-semibold text-slate-800">{displayCurrency(row[sectionConfig.totalField])}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{row.expectedClosing}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{row.closingQuantity}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${isVarianceValid ? 'text-emerald-700' : 'text-rose-700'}`}>{row.variance}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
                        isVarianceValid ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
                      }`}>
                        {!isVarianceValid ? <AlertTriangle className="h-3.5 w-3.5" /> : null}
                        {isVarianceValid ? 'Valid' : 'Variance'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!filteredRows.length ? (
          <div className="mt-4 rounded-[12px] border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            {!hasStoreContext
              ? 'Select a specific store from the header to load produce inventory.'
              : products.length
                ? 'No produce rows match the current search.'
                : 'No produce products are classified for this store yet.'}
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            onClick={() => setRows((current) => clearProduceField(current, sectionConfig.quantityField))}
            disabled={!hasStoreContext || !rows.length}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" />
            Clear Quantity
          </button>
          <button
            type="button"
            onClick={() => setRows((current) => clearProduceField(current, sectionConfig.rateField))}
            disabled={!hasStoreContext || !rows.length}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <RotateCcw className="h-4 w-4" />
            Clear Price
          </button>
          <button
            type="button"
            onClick={openSaveProgressModal}
            disabled={!hasStoreContext || !rows.length || savingProgress}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-amber-500/20 transition hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            Save Progress
          </button>
          <button
            type="button"
            onClick={() => handleSave('completed')}
            disabled={!hasStoreContext || !rows.length}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Save className="h-4 w-4" />
            Complete Task
          </button>
        </div>
      </div>

      <div className="rounded-[16px] border border-slate-200 bg-white p-5 shadow-[0_14px_40px_rgba(15,23,42,0.05)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Saved Produce Days</h2>
            <p className="mt-1 text-sm text-slate-500">
              Showing saved days between {formatDateLabel(savedFromDate)} and {formatDateLabel(savedToDate)}. Reopen any day to edit and save again.
            </p>
          </div>
        </div>

        <div className="mt-4 table-scroll-shell">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead>
              <tr className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-3 font-semibold">Date</th>
                <th className="px-3 py-3 font-semibold">Store</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Products</th>
                <th className="px-3 py-3 font-semibold text-right">Purchase Qty</th>
                <th className="px-3 py-3 font-semibold text-right">Purchase Amount</th>
                <th className="px-3 py-3 font-semibold text-right">Variance</th>
                <th className="px-3 py-3 font-semibold">Updated</th>
                <th className="px-3 py-3 font-semibold text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {savedRecords.map((record) => {
                const recordSummary = calculateProduceSummary(record.rows || []);
                return (
                  <tr key={record.id} className="hover:bg-slate-50/70">
                    <td className="px-3 py-3 text-slate-700">{formatDateLabel(record.date)}</td>
                    <td className="px-3 py-3 text-slate-700">{record.storeName}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${record.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {record.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-slate-700">{recordSummary.totalProducts}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{recordSummary.purchasedUnits}</td>
                    <td className="px-3 py-3 text-right text-slate-700">{displayCurrency(recordSummary.purchasedAmount)}</td>
                    <td className={`px-3 py-3 text-right font-semibold ${recordSummary.varianceTotal === 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{recordSummary.varianceTotal}</td>
                    <td className="px-3 py-3 text-slate-500">{record.updatedAt ? new Date(record.updatedAt).toLocaleString() : '-'}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openRecordForEdit(record)}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {!savedRecords.length ? (
          <div className="mt-4 rounded-[12px] border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
            No saved produce tracker days found in the selected date range.
          </div>
        ) : null}
      </div>

      <Modal
        isOpen={invoiceModalOpen}
        onClose={savingProgress ? undefined : () => setInvoiceModalOpen(false)}
        size="lg"
      >
        <div className="px-5 py-5 sm:px-6">
          <h3 className="text-lg font-semibold text-slate-900">Purchase Invoice Confirmation</h3>
          <p className="mt-1 text-sm text-slate-600">
            Enter invoice details before saving progress. Purchase SKU total is {displayCurrency(summary.purchasedAmount)}.
          </p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="label">Invoice Number *</span>
              <input
                className="input rounded-lg"
                type="text"
                value={invoiceForm.invoiceNumber}
                onChange={(event) => setInvoiceForm((current) => ({ ...current, invoiceNumber: event.target.value }))}
                placeholder="Enter invoice number"
              />
              {invoiceErrors.invoiceNumber ? (
                <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.invoiceNumber}</span>
              ) : null}
            </label>
            <label className="block">
              <span className="label">Total Amount *</span>
              <input
                className="input rounded-lg"
                type="number"
                min="0"
                step="0.01"
                value={invoiceForm.totalAmount}
                onChange={(event) => setInvoiceForm((current) => ({ ...current, totalAmount: event.target.value }))}
                placeholder="0.00"
              />
              {invoiceErrors.totalAmount ? (
                <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.totalAmount}</span>
              ) : null}
            </label>
          </div>

          <div className="mt-5 space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">
                  1) Is the total amount of invoice matches with the Purchase SKU total Amount?
                </p>
                <YesNoToggle
                  name="sku-total"
                  value={invoiceForm.skuTotalMatches}
                  onChange={(value) => setInvoiceForm((current) => ({
                    ...current,
                    skuTotalMatches: value,
                    skuTotalMismatchReason: value ? '' : current.skuTotalMismatchReason,
                  }))}
                />
              </div>
              {invoiceErrors.skuTotalMatches ? (
                <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.skuTotalMatches}</span>
              ) : null}
              {invoiceForm.skuTotalMatches === false ? (
                <label className="mt-3 block">
                  <span className="label">Reason *</span>
                  <textarea
                    className="input min-h-[72px] rounded-lg"
                    value={invoiceForm.skuTotalMismatchReason}
                    onChange={(event) => setInvoiceForm((current) => ({
                      ...current,
                      skuTotalMismatchReason: event.target.value,
                    }))}
                    placeholder="Explain why invoice total does not match Purchase SKU total"
                  />
                  {invoiceErrors.skuTotalMismatchReason ? (
                    <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.skuTotalMismatchReason}</span>
                  ) : null}
                </label>
              ) : null}
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm font-semibold text-slate-800">
                  2) Is the invoice amount Matches seller system?
                </p>
                <YesNoToggle
                  name="seller-system"
                  value={invoiceForm.sellerSystemMatches}
                  onChange={(value) => setInvoiceForm((current) => ({
                    ...current,
                    sellerSystemMatches: value,
                    sellerSystemMismatchReason: value ? '' : current.sellerSystemMismatchReason,
                    sellerSystemAmount: value ? '' : current.sellerSystemAmount,
                  }))}
                />
              </div>
              {invoiceErrors.sellerSystemMatches ? (
                <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.sellerSystemMatches}</span>
              ) : null}
              {invoiceForm.sellerSystemMatches === false ? (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block sm:col-span-2">
                    <span className="label">Reason *</span>
                    <textarea
                      className="input min-h-[72px] rounded-lg"
                      value={invoiceForm.sellerSystemMismatchReason}
                      onChange={(event) => setInvoiceForm((current) => ({
                        ...current,
                        sellerSystemMismatchReason: event.target.value,
                      }))}
                      placeholder="Explain why invoice amount does not match seller system"
                    />
                    {invoiceErrors.sellerSystemMismatchReason ? (
                      <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.sellerSystemMismatchReason}</span>
                    ) : null}
                  </label>
                  <label className="block">
                    <span className="label">Amount shown in seller system *</span>
                    <input
                      className="input rounded-lg"
                      type="number"
                      min="0"
                      step="0.01"
                      value={invoiceForm.sellerSystemAmount}
                      onChange={(event) => setInvoiceForm((current) => ({
                        ...current,
                        sellerSystemAmount: event.target.value,
                      }))}
                      placeholder="0.00"
                    />
                    {invoiceErrors.sellerSystemAmount ? (
                      <span className="mt-1 block text-xs text-danger-600">{invoiceErrors.sellerSystemAmount}</span>
                    ) : null}
                  </label>
                </div>
              ) : null}
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setInvoiceModalOpen(false)}
              disabled={savingProgress}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={confirmSaveProgress}
              disabled={savingProgress}
            >
              {savingProgress ? 'Saving...' : 'Confirm & Save Progress'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProduceInventoryTracker;
